import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { auth } from "../../firebase/firebaseConfig";
import { getApiUrl } from "../../utils/api";

const getAuthHeaders = async () => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error("No hay sesión activa.");
  }

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

const apiRequest = async (endpoint, options = {}) => {
  const headers = await getAuthHeaders();
  const response = await fetch(getApiUrl(endpoint), {
    ...options,
    headers: {
      ...headers,
      ...(options.headers || {}),
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error || "No se pudo completar la operación.");
  }

  return data;
};

const BrevoAdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [maskedApiKey, setMaskedApiKey] = useState("");
  const [defaultListId, setDefaultListId] = useState("");
  const [hasConfig, setHasConfig] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

  const showMessage = (severity, message) => {
    setSnackbar({ open: true, severity, message });
  };

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await apiRequest("brevoGetConfigHttp");
      setHasConfig(Boolean(data.hasConfig));
      setMaskedApiKey(data.maskedApiKey || "");
      setDefaultListId(data.defaultListId ? String(data.defaultListId) : "");
      setLastSync(data.lastSync || null);
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const data = await apiRequest("brevoSaveConfigHttp", {
        method: "POST",
        body: JSON.stringify({
          apiKey,
          defaultListId: defaultListId ? Number(defaultListId) : null,
        }),
      });

      setHasConfig(true);
      setMaskedApiKey(data.maskedApiKey || "");
      setApiKey("");
      showMessage("success", "Configuración de Brevo guardada.");
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const data = await apiRequest("brevoTestConnectionHttp", { method: "POST", body: "{}" });
      setDiagnostics(data);
      if (!defaultListId && data.defaultListId) {
        setDefaultListId(String(data.defaultListId));
      }
      showMessage("success", "Conexión con Brevo verificada.");
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setTesting(false);
    }
  };

  const handleSyncMembers = async () => {
    setSyncing(true);
    try {
      const data = await apiRequest("brevoSyncMembersHttp", { method: "POST", body: "{}" });
      setLastSync(data.summary || null);
      showMessage(
        "success",
        `Sincronización completada. Sincronizados: ${data.summary?.synced || 0}. Fallidos: ${data.summary?.failed || 0}.`
      );
    } catch (error) {
      showMessage("error", error.message);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Integración Brevo
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configurá la API key de Brevo, probá la conexión y sincronizá todos los socios actuales a la plataforma.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuración
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="API Key de Brevo"
                  type="password"
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                  placeholder={hasConfig ? "Ingresá una nueva clave para reemplazar la actual" : "xkeysib-..."}
                  fullWidth
                />
                <TextField
                  label="Lista por defecto en Brevo"
                  value={defaultListId}
                  onChange={(event) => setDefaultListId(event.target.value)}
                  select={Boolean(diagnostics?.lists?.length)}
                  fullWidth
                  helperText="Opcional. Si elegís una lista, todos los socios sincronizados se agregarán a esa lista."
                >
                  {diagnostics?.lists?.map((list) => (
                    <MenuItem key={list.id} value={String(list.id)}>
                      {list.name} ({list.id})
                    </MenuItem>
                  ))}
                </TextField>
                {hasConfig && (
                  <Alert severity="info">
                    Clave guardada actualmente: <strong>{maskedApiKey}</strong>
                  </Alert>
                )}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    variant="contained"
                    onClick={handleSaveConfig}
                    disabled={saving || (!apiKey.trim() && !hasConfig)}
                  >
                    {saving ? "Guardando..." : "Guardar configuración"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleTestConnection}
                    disabled={testing || !hasConfig}
                  >
                    {testing ? "Probando..." : "Probar conexión"}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sincronización
              </Typography>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  La sincronización usa la colección <code>miembros</code> como fuente y crea o actualiza contactos en Brevo usando el email como identificador principal.
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSyncMembers}
                  disabled={syncing || !hasConfig}
                >
                  {syncing ? "Sincronizando..." : "Enviar todos los miembros a Brevo"}
                </Button>
                {lastSync && (
                  <Alert severity={lastSync.failed ? "warning" : "success"}>
                    Última sincronización: procesados {lastSync.processed || 0}, sincronizados {lastSync.synced || 0}, sin email {lastSync.skippedWithoutEmail || 0}, fallidos {lastSync.failed || 0}.
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {diagnostics && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Estado de Brevo
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Cuenta</Typography>
                <Typography variant="body2">
                  {diagnostics.account?.companyName || "-"}
                </Typography>
                <Typography variant="body2">
                  {diagnostics.account?.email || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Listas disponibles</Typography>
                <Typography variant="body2">
                  {diagnostics.lists?.length || 0} listas detectadas
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Atributos disponibles</Typography>
                <Typography variant="body2">
                  {diagnostics.attributes?.length || 0} atributos detectados
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Atributos de contacto detectados en Brevo
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {(diagnostics.attributes || [])
                .map((attribute) => attribute.name)
                .filter(Boolean)
                .sort()
                .join(", ") || "No se encontraron atributos."}
            </Typography>
          </CardContent>
        </Card>
      )}

      {lastSync?.sampleFailures?.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Fallos de la última sincronización
            </Typography>
            <Stack spacing={1}>
              {lastSync.sampleFailures.map((failure, index) => (
                <Alert key={`${failure.memberId}-${index}`} severity="warning">
                  {failure.memberId} ({failure.email || "sin email"}): {failure.error}
                </Alert>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BrevoAdminPage;
